"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Phone, MapPin, Flame, Heart } from "lucide-react"
import { getTokenFromStorage, getLocalIdFromToken } from "@/lib/auth"

export default function VibrantMenuHero() {
    const [currentImage, setCurrentImage] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const [greeting, setGreeting] = useState("Olá")
    const [data, setData] = useState<any>(null)
    const [localId, setLocalId] = useState<string | null>(null)

    useEffect(() => {
        const token = getTokenFromStorage()
        if (token) {
            setLocalId(getLocalIdFromToken(token))
        }
    }, [])

    useEffect(() => {
        const hour = new Date().getHours()
        if (hour < 12) setGreeting("Bom dia")
        else if (hour < 18) setGreeting("Boa tarde")
        else setGreeting("Boa noite")
    }, [])

    useEffect(() => {
        if (!localId) return

        const cached = localStorage.getItem(`localData_${localId}`)
        if (cached) setData(JSON.parse(cached))

        fetch(`https://api.syncmenu.ao/generic/estabelecimento/${localId}`)
            .then((res) => res.json())
            .then((json) => {
                const content = json.localDeAtendimento
                setData(content)
                localStorage.setItem(`localData_${localId}`, JSON.stringify(content))
            })
            .catch(console.error)
    }, [localId])

    useEffect(() => {
        if (!data?.secaoId?.urls || data.secaoId.urls.length <= 1) return
        const interval = setInterval(() => {
            setLoaded(false)
            setTimeout(() => {
                setCurrentImage((prev) => (prev + 1) % data.secaoId.urls.length)
            }, 400)
        }, 10000)
        return () => clearInterval(interval)
    }, [data])

    if (!data) {
        return (
            <div className="h-[70vh] w-full bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 flex items-center justify-center">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    className="w-16 h-16 border-4 border-white border-t-transparent rounded-full"
                />
            </div>
        )
    }

    const imagens = data.secaoId.urls
    const imageUrl = imagens[currentImage] || imagens[0]

    return (
        <div className="relative h-[70vh] w-full overflow-hidden rounded-b-[3rem] shadow-2xl">
            {/* Vibrant Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500" />

            {/* Image Overlay */}
            <div className="absolute inset-0">
                <img
                    src={imageUrl || "/placeholder.svg"}
                    alt="Hero"
                    className={`object-cover w-full h-full mix-blend-overlay transition-all duration-1000 ${loaded ? "opacity-70" : "opacity-50 scale-105"
                        }`}
                    onLoad={() => setLoaded(true)}
                    onError={(e) => {
                        e.currentTarget.src = "/placeholder.svg"
                    }}
                />
            </div>

            {/* Floating Elements */}
            <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className="absolute top-20 right-10 w-20 h-20 bg-white/20 rounded-full blur-xl"
            />
            <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                className="absolute bottom-40 left-20 w-32 h-32 bg-purple-300/20 rounded-full blur-xl"
            />

            {/* Content */}
            <div className="relative z-10 h-full w-full flex flex-col justify-between px-6 md:px-12 py-8 text-white">
                {/* Top Section */}
                <div className="flex items-start justify-between">
                    <motion.div
                        initial={{ x: -50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8, type: "spring" }}
                    >
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-lg rounded-full px-4 py-2 mb-4">
                            <Flame className="w-4 h-4 text-yellow-300" />
                            <span className="text-sm font-semibold">{greeting}!</span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-black mb-2 text-shadow-lg">
                            {data.estabelecimentoId.nome}
                        </h1>

                        <p className="text-xl text-white/90 font-bold flex items-center gap-2">
                            <Heart className="w-5 h-5 text-pink-300 fill-pink-300" />
                            {data.secaoId.nome}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.6, type: "spring", bounce: 0.5 }}
                        className="bg-gradient-to-br from-yellow-300 to-orange-400 rounded-3xl p-4 shadow-2xl"
                    >
                        <MapPin className="w-8 h-8 text-white" />
                    </motion.div>
                </div>

                {/* Bottom Section */}
                <div>
                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        className="text-lg md:text-xl font-semibold mb-6 text-white drop-shadow-lg"
                    >
                        🎉 Onde cada refeição é uma celebração! Venha saborear momentos inesquecíveis!
                    </motion.p>

                    {/* Info Cards - Colorful & Rounded */}
                    <div className="flex flex-wrap gap-3">
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            whileHover={{ scale: 1.05, rotate: -2 }}
                            className="flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full px-5 py-3 shadow-lg"
                        >
                            <Phone className="w-5 h-5" />
                            <span className="font-bold text-sm">{data.estabelecimentoId.telefone || "Ligue Agora!"}</span>
                        </motion.div>

                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.7 }}
                            whileHover={{ scale: 1.05, rotate: -2 }}
                            className="flex items-center gap-2 bg-gradient-to-r from-pink-400 to-red-500 rounded-full px-5 py-3 shadow-lg"
                        >
                            <MapPin className="w-5 h-5" />
                            <span className="font-bold text-sm">{data.nome}</span>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    )
}
